// Dubai guide: AC ton size guide. Rules of thumb only.
const content = {
    slug: 'ac-ton-size-guide',
    title: 'AC Ton Size Guide for Dubai | Which Tonnage for Your Room?',
    description: 'How many tons of AC does your room need in Dubai? A simple size table, the factors that push it up, and signs your unit is undersized.',
    eyebrow: 'Dubai · AC guide',
    h1: 'AC Ton Size Guide: Which Tonnage Does Your Room Need?',
    lead: 'In the UAE, AC capacity is sold in tons. Too small and the unit runs all afternoon without cooling the room; too big and it cools fast but leaves the air clammy. Here is a practical way to size it for Dubai conditions.',
    datePublished: '2026-10-03',
    image: 'aircon-front.jpg',
    imageAlt: 'Wall-mounted AC unit with its capacity label',
    summary: {
        title: 'Key numbers',
        items: [
            '1 ton of cooling = 12,000 BTU per hour.',
            'Typical Dubai bedroom (120-150 sq ft): 1 ton.',
            'Add capacity for top floors, west-facing glass and kitchens.',
        ],
    },
    sections: [
        {
            title: 'Rule-of-thumb size table',
            table: {
                columns: [
                    { key: 'room', label: 'Room size' },
                    { key: 'ton', label: 'Suggested capacity' },
                    { key: 'example', label: 'Typical room' },
                ],
                rows: [
                    { room: 'Up to 120 sq ft (11 m²)', ton: '0.75 - 1 ton', example: 'Small bedroom, study' },
                    { room: '120 - 180 sq ft (11-17 m²)', ton: '1 - 1.5 ton', example: 'Standard bedroom' },
                    { room: '180 - 280 sq ft (17-26 m²)', ton: '1.5 - 2 ton', example: 'Master bedroom, small living room' },
                    { room: '280 - 400 sq ft (26-37 m²)', ton: '2 - 2.5 ton', example: 'Living and dining' },
                    { room: '400 sq ft + (37 m² +)', ton: '3 ton +, or two units', example: 'Open-plan villa living area' },
                ],
            },
            note: 'These are rules of thumb for Dubai\'s climate. A proper heat-load calculation by an HVAC engineer is the right way to size a new installation.',
        },
        {
            title: 'What pushes the size up',
            cards: [
                { title: 'Sun and glass', body: 'West or south-facing floor-to-ceiling glass can add a large share to the load in the afternoon.' },
                { title: 'Top floor or villa roof', body: 'Heat soaks through the roof slab; add capacity for top-floor rooms.' },
                { title: 'Kitchens and gyms', body: 'Cooking and exercise add heat; size these rooms up.' },
                { title: 'Open doors', body: 'Rooms open to corridors or stairwells cool a much larger volume of air.' },
            ],
        },
        {
            title: 'Signs your unit is the wrong size',
            paragraphs: [
                'Undersized: the unit runs without stopping on summer afternoons and the room stays above the set temperature, even right after a cleaning. Oversized: it cools quickly then switches off, switches on again a few minutes later, and the room feels damp. Before replacing anything, rule out a dirty coil or low gas - they mimic an undersized unit.',
            ],
        },
    ],
    faqs: [
        {
            question: 'What size AC for a 12x12 ft room in Dubai?',
            answer: 'A 144 sq ft room usually needs about 1 ton, or 1.5 ton if it is on the top floor or has large sun-facing windows.',
        },
        {
            question: 'Is a bigger AC better?',
            answer: 'No. An oversized unit cycles on and off and removes less humidity, so the room feels clammy and the unit wears faster.',
        },
    ],
    cta: {
        title: 'Not cooling enough? Check the coil first',
        body: 'A dirty coil can make a correctly sized unit perform like an undersized one.',
        href: '/ae/ac-service-dubai/ac-not-cooling',
        label: 'AC not cooling: causes and fixes',
    },
}

export default content
